import {
  CLASSIFIER_COMMAND,
  gatewayModel,
  isGatewayAiCommand,
  readStepModel,
  stepSpanName,
} from '../../utils/gateway-models';
import { passOf } from './engine-timeline';
import type { EngineRequest, EngineStep } from './validate-api';

/**
 * The AI calls in a reply's trace: which model each AI step called through
 * the AI Gateway and how long the step took. The model is the one the step
 * asks for in Pro Studio now (the engine logs don't carry it), which can
 * differ from the published version that answered.
 *
 * A Decisions step followed by the Supervisor's own model step in the same
 * walk fell back: the classifier failed or timed out.
 */

export interface TraceRow {
  nodeNumber?: number;
  command?: unknown;
  parameterInput?: unknown;
  whatsNext?: unknown;
}

export interface TraceAiCall {
  step: EngineStep;
  /** "OpenAI Decisions · gpt-6-luna", "Gemini 3 Flash", or the raw id. */
  model: string;
  /** The id the gateway is sent ("default" resolved). */
  modelId: string;
  provider?: string;
  decisions: boolean;
  durationMs?: number;
  span?: string;
  /** A Decisions step whose walk went on to its fallback model. */
  fellBack?: boolean;
}

export const traceAiCalls = (
  requests: EngineRequest[],
  rowFor: (node: number) => TraceRow | undefined
): TraceAiCall[] =>
  requests.flatMap((req) => {
    const durations = new Map(
      passOf(req).bars.map((b) => [b.step, b.durationMs] as const)
    );
    const calls = req.steps.flatMap((step): TraceAiCall[] => {
      const row = rowFor(step.node);
      const command = step.action ?? row?.command;
      if (!row || !isGatewayAiCommand(command)) return [];
      const id = readStepModel(row.parameterInput);
      const model = gatewayModel(id, command);
      return [
        {
          step,
          model: model?.label ?? id,
          modelId: model?.id ?? id,
          provider: model?.provider,
          decisions: command === CLASSIFIER_COMMAND,
          durationMs: durations.get(step),
          span: stepSpanName(row.parameterInput),
        },
      ];
    });
    // Fell back: the next step is one the classifier sends its false /
    // timeout / error outcomes to.
    calls.forEach((call) => {
      if (!call.decisions) return;
      const after = req.steps[req.steps.indexOf(call.step) + 1];
      const routes = rowFor(call.step.node)?.whatsNext;
      const fallbacks = (Array.isArray(routes) ? routes : [])
        .map(String)
        .filter((w) => ['false', 'timeout', 'error'].includes(w.split('~')[0]))
        .map((w) => Number(w.split('~')[1]));
      call.fellBack = !!after && fallbacks.includes(after.node);
    });
    return calls;
  });

/** Where the env's own Langfuse is, from the admin API host. */
export const langfuseHome = (adminUrl: string | undefined): string => {
  const env = adminUrl?.match(
    /\.(main|candidate|uat|qa1)\.pypestream\.dev/
  )?.[1];
  return env
    ? `https://langfuse.${env}.pypestream.dev`
    : 'https://langfuse.pypestream.com';
};
